import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import CTA from "../components/CTA";
import { articles } from "../data/articles";
import { industryDetails } from "../data/industries";
import { ArrowLeft, ArrowRight, Clock, Calendar, BookOpen } from "lucide-react";

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
  } catch {
    return iso;
  }
}

export default function ArticlePage() {
  const { slug } = useParams();
  const article = articles.find((a) => a.slug === slug);
  if (!article) return <Navigate to="/resources" replace />;

  const industry = industryDetails[article.industry];
  const related = articles.filter((a) => a.slug !== article.slug).slice(0, 3);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="pt-28">
        {/* Hero */}
        <section className="relative overflow-hidden">
          <div className="pointer-events-none absolute left-1/2 top-[60px] -z-10 h-[320px] w-[640px] -translate-x-1/2 rounded-full bg-[#d4af37]/10 blur-[120px]" />
          <div className="mx-auto max-w-3xl px-5 py-12 lg:px-8 lg:py-16">
            <Link to="/resources" className="inline-flex items-center gap-1.5 text-xs font-medium uppercase tracking-[0.14em] text-slate-500 hover:text-[#1e1b4b]">
              <ArrowLeft className="h-3.5 w-3.5" /> All articles
            </Link>

            {industry && (
              <Link
                to={`/industries/${industry.slug}`}
                className="mt-6 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-600 transition-colors hover:border-[#d4af37]/40 hover:text-[#1e1b4b]"
              >
                <span className="h-1.5 w-1.5 rounded-full bg-[#d4af37]" />
                {industry.name}
              </Link>
            )}

            <h1 className="mt-5 text-4xl font-medium tracking-tight text-[#1e1b4b] sm:text-5xl">
              {article.title}
            </h1>
            <p className="mt-5 text-[18px] leading-relaxed text-slate-600">{article.excerpt}</p>

            <div className="mt-7 flex flex-wrap items-center gap-5 text-xs text-slate-500">
              <span className="inline-flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5" />
                {fmtDate(article.date)}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5" />
                {article.readTime} min read
              </span>
              <span className="inline-flex items-center gap-1.5">
                <BookOpen className="h-3.5 w-3.5" />
                {article.author}
              </span>
            </div>
          </div>
        </section>

        {/* Body */}
        <article className="border-t border-slate-200/80 bg-white py-14">
          <div className="mx-auto max-w-3xl px-5 lg:px-8">
            <div className="prose prose-slate max-w-none">
              {article.body.map((block, i) => {
                if (block.type === "h2") {
                  return (
                    <h2
                      key={i}
                      className="mt-10 text-2xl font-semibold tracking-tight text-[#1e1b4b]"
                    >
                      {block.text}
                    </h2>
                  );
                }
                return (
                  <p
                    key={i}
                    className="mt-5 text-[16px] leading-[1.75] text-slate-700"
                  >
                    {block.text}
                  </p>
                );
              })}
            </div>

            {/* Author + CTA bar */}
            <div className="mt-14 rounded-2xl border border-slate-200 bg-[#faf8f3] p-7">
              <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-[10.5px] font-semibold uppercase tracking-[0.18em] text-[#d4af37]">
                    Put this playbook to work
                  </p>
                  <p className="mt-2 text-base font-semibold text-[#1e1b4b]">
                    Want help running this for your business?
                  </p>
                </div>
                <Link
                  to="/demo"
                  className="group inline-flex items-center gap-2 whitespace-nowrap rounded-xl bg-[#1e1b4b] px-5 py-3 text-sm font-semibold text-white hover:bg-[#1e1b4b]/92"
                >
                  Talk to a strategist
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </Link>
              </div>
            </div>
          </div>
        </article>

        {/* Related */}
        {related.length > 0 && (
          <section className="border-t border-slate-200/80 bg-slate-50/40 py-16">
            <div className="mx-auto max-w-6xl px-5 lg:px-8">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                Keep reading
              </p>
              <h2 className="mt-2 text-3xl font-medium tracking-tight text-[#1e1b4b]">
                More playbooks.
              </h2>
              <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-3">
                {related.map((r) => {
                  const ind = industryDetails[r.industry];
                  return (
                    <Link
                      key={r.slug}
                      to={`/resources/${r.slug}`}
                      className="group flex flex-col rounded-2xl border border-slate-200 bg-white p-6 transition-all hover:-translate-y-1 hover:shadow-md"
                    >
                      {ind && (
                        <span className="inline-flex w-fit items-center rounded-full bg-[#1e1b4b]/[0.06] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-[#1e1b4b]">
                          {ind.name}
                        </span>
                      )}
                      <h3 className="mt-4 text-base font-semibold leading-snug text-[#1e1b4b]">
                        {r.title}
                      </h3>
                      <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-[#1e1b4b] group-hover:text-[#d4af37]">
                        Read
                        <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
                      </span>
                    </Link>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        <CTA />
      </main>
      <Footer />
    </div>
  );
}
